import React, { useState, useEffect } from 'react'
import { FormattedMessage } from 'react-intl'
import {
 Typography, Radio, RadioGroup, FormControlLabel, FormControl, Paper, Dialog, DialogContent, DialogTitle, IconButton,
} from '@material-ui/core'
import CloseIcon from '@material-ui/icons/Close'
import { useParams } from 'react-router-dom'
import { useSelector } from 'react-redux'
import Button from '../../../../components/Buttons/Button'
import { useTenantConfigs } from '../../../../hooks/useTenantConfigs'
import { biometryResendService } from '../../../../services/people'
import { useStyles } from './BiometryResend.styles'

const BiometryResendForm = ({ onBack, prospectData }) => {
  const [isLoading, setIsLoading] = useState(false)
  const [selectedOption, setSelectedOption] = useState('sms')
  const [showSuccess, setShowSuccess] = useState(false)
  const [showError, setShowError] = useState(false)
  const [showResendOptions, setShowResendOptions] = useState(false)
  const [showCountdown, setShowCountdown] = useState(false)
  const [countdown, setCountdown] = useState(0)

  const { configs } = useTenantConfigs()
  const { id } = useParams()
  const classes = useStyles()
  const { user: { data } } = useSelector(({ user }) => user)

  const mapPhonePriority = (phones) => {
    if (!phones || phones.length === 0) return []

    const sortedPhones = phones.sort((a, b) => {
      const dateA = new Date(a.updatedAt || 0).getTime()
      const dateB = new Date(b.updatedAt || 0).getTime()
      return dateB - dateA
    })

    const prioritizedPhones = sortedPhones.sort((a, b) => {
      const isAPriority = a.type === 'WHATSAPP' || a.type === 'MOBILE'
      const isBPriority = b.type === 'WHATSAPP' || b.type === 'MOBILE'

      if (isAPriority && !isBPriority) return -1
      if (!isAPriority && isBPriority) return 1
      return 0
    })

    return prioritizedPhones.slice(0, 2)
  }

  const prioritizedPhones = mapPhonePriority(prospectData?.phones || [])
  const rawPhoneNumber = prioritizedPhones[0]?.number?.trim() || 'Número não disponível'

  const formatPhoneNumber = (phone) => {
    if (phone === 'Número não disponível') return phone
    const cleanPhone = phone.replace(/\s+/g, ' ').trim()
    const parts = cleanPhone.split(' ')
    if (parts.length >= 2) {
      return `(${parts[0]}) ${parts.slice(1).join(' ')}`
    }
    return phone
  }

  const phoneNumber = formatPhoneNumber(rawPhoneNumber)

  useEffect(() => {
    let interval = null
    if (countdown > 0 && showCountdown) {
      interval = setInterval(() => {
        setCountdown((prev) => {
          const newValue = prev - 1
          if (newValue <= 0) {
            setShowCountdown(false)
            setShowResendOptions(true)
            return 0
          }
          return newValue
        })
      }, 1000)
    }
    return () => clearInterval(interval)
  }, [countdown, showCountdown])

  const sendBiometryLink = async (methodParam) => {
    const method = (typeof methodParam === 'string') ? methodParam : selectedOption

    setIsLoading(true)
    setShowError(false)
    setShowSuccess(false)
    setShowCountdown(false)
    setShowResendOptions(false)

    if (!configs?.company?.id || !configs?.country?.alpha2Code) {
      setShowError(true)
      setIsLoading(false)
      return
    }

    try {
      await biometryResendService({
        personid: id,
        companyid: configs.company.id,
        countryid: configs.country.alpha2Code,
        method,
        phoneNumber: rawPhoneNumber,
        responsibleId: data?.personId,
      })

      // Primeiro mostra o modal com countdown
      setShowResendOptions(false)
      setShowSuccess(false)
      setCountdown(30)
      setShowCountdown(true)
    } catch (error) {
      setShowError(true)
    } finally {
      setIsLoading(false)
    }
  }

  const handleBack = () => {
    setShowSuccess(false)
    setShowError(false)
    setShowCountdown(false)
    onBack()
  }

  const handleCloseSuccess = () => {
    setShowSuccess(false)
    setCountdown(0)
    onBack()
  }

  const handleCloseCountdown = () => {
    setShowCountdown(false)
    setCountdown(0)
    onBack()
  }

  const handleCloseError = () => {
    setShowError(false)
  }

  const handleCloseResendOptions = () => {
    setShowResendOptions(false)
  }

  const handleResendViaSMS = async () => {
    setShowResendOptions(false)
    setShowSuccess(false)
    setShowCountdown(false)
    setCountdown(0)
    setSelectedOption('sms')
    await sendBiometryLink('sms')
  }

  const handleResendViaWhatsapp = async () => {
    setShowResendOptions(false)
    setShowSuccess(false)
    setShowCountdown(false)
    setCountdown(0)
    setSelectedOption('whatsapp')
    await sendBiometryLink('whatsapp')
  }

  const handleTryAgain = () => {
    setShowError(false)
    onBack()
  }

  return (
    <>
      <div className={classes.fullScreenContainer}>
        <Paper className={classes.fullScreenPaper}>
          <div className={classes.contentWrapper}>
            <Typography variant="h5" className={classes.fullScreenTitle}>
              <FormattedMessage id="biometryVerificationTitle" defaultMessage="Verificação de identidade por biometria facial" />
            </Typography>

            <Typography variant="body1" className={classes.fullScreenDescription}>
              <FormattedMessage
                id="biometryVerificationDescription"
                defaultMessage="Para que a Consultora de Beleza finalize seu cadastro, será necessário realizar uma verificação de identidade com biometria facial. Escolha abaixo como deseja enviar o link para a consultora:"
              />
            </Typography>

            <FormControl component="fieldset" className={classes.fullScreenRadioGroup}>
              <RadioGroup
                value={selectedOption}
                onChange={(e) => setSelectedOption(e.target.value)}
              >
                <FormControlLabel
                  value="sms"
                  control={<Radio className={classes.radioButton} />}
                  label={(
                    <span>
                      Enviar link via
                      {' '}
                      <strong>SMS</strong>
                      {' '}
                      para
                      {' '}
                      <strong>{phoneNumber}</strong>
                    </span>
                  )}
                />
                <FormControlLabel
                  value="whatsapp"
                  control={<Radio className={classes.radioButton} />}
                  label={(
                    <span>
                      Enviar link via
                      {' '}
                      <strong>Whatsapp</strong>
                      {' '}
                      para
                      {' '}
                      <strong>{phoneNumber}</strong>
                    </span>
                  )}
                />
              </RadioGroup>
            </FormControl>
          </div>
        </Paper>

        <div className={classes.fullScreenButtons}>
          <Button
            variant="outlined"
            color="primary"
            style={{ textTransform: 'none' }}
            onClick={handleBack}
            disabled={isLoading}
          >
            voltar
          </Button>
          <Button
            variant="contained"
            color="primary"
            onClick={sendBiometryLink}
            disabled={isLoading}
            style={{ textTransform: 'none' }}
          >
            {isLoading ? 'enviando...' : 'enviar link'}
          </Button>
        </div>
      </div>

      {/* Countdown Modal - Primeiro envio */}
      <Dialog open={showCountdown} onClose={handleCloseCountdown} className={classes.modal}>
        <DialogTitle className={classes.modalTitle}>
          Link enviado com sucesso
          <IconButton onClick={handleCloseCountdown} className={classes.closeButton}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent className={classes.modalContent}>
          <Typography variant="body1" className={classes.modalDescription}>
            O link para realizar a biometria facial foi enviado para a consultora no número
            {' '}
            {phoneNumber}
            .
          </Typography>

          <Typography variant="body2" className={classes.countdownText}>
            <span className={classes.clockIcon}>⏱</span>
            {' '}
            Espere
            {' '}
            {countdown}
            {' '}
            segundos para reenviar novamente.
          </Typography>

          <div className={classes.modalButtons}>
            <Button variant="outlined" color="primary" onClick={handleCloseCountdown}>
              voltar
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Success Modal */}
      <Dialog open={showSuccess} onClose={handleCloseSuccess} className={classes.modal}>
        <DialogTitle className={classes.modalTitle}>
          Link enviado com sucesso
          <IconButton onClick={handleCloseSuccess} className={classes.closeButton}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent className={classes.modalContent}>
          <Typography variant="body1" className={classes.modalDescription}>
            O link para realizar a biometria facial foi enviado para a consultora no número
            {' '}
            {phoneNumber}
            .
          </Typography>

          <div className={classes.modalButtons}>
            <Button variant="outlined" color="primary" onClick={handleCloseSuccess}>
              voltar
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Error Modal */}
      <Dialog open={showError} onClose={handleCloseError} className={classes.modal}>
        <DialogTitle className={classes.modalTitle}>
          Erro ao enviar o link
          <IconButton onClick={handleCloseError} className={classes.closeButton}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent className={classes.modalContent}>
          <Typography variant="body1" className={classes.modalDescription}>
            Não foi possível enviar o link de biometria facial para o número
            {' '}
            {phoneNumber}
            .
          </Typography>

          <div className={classes.modalButtons}>
            <Button variant="outlined" onClick={handleTryAgain} color="primary">
              tentar novamente
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Resend Options Modal */}
      <Dialog open={showResendOptions} onClose={handleCloseResendOptions} className={classes.modal}>
        <DialogTitle className={classes.modalTitle}>
          Link enviado com sucesso
          <IconButton onClick={handleCloseResendOptions} className={classes.closeButton}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent className={classes.modalContent}>
          <Typography variant="body1" className={classes.modalDescription}>
            O link para realizar a biometria facial foi enviado para a consultora no número
            {' '}
            {phoneNumber}
            .
          </Typography>

          <Typography variant="body2" className={classes.resendQuestion}>
            Não recebeu o link?
          </Typography>

          <div className={classes.resendButtonsContainer}>
            <Button
              variant="outlined"
              color="primary"
              onClick={handleResendViaSMS}
              className={classes.resendOptionButton}
              disabled={isLoading}
            >
              reenviar código por SMS
            </Button>
            <Button
              variant="outlined"
              color="primary"
              onClick={handleResendViaWhatsapp}
              className={classes.resendOptionButton}
              disabled={isLoading}
            >
              enviar código por whatsapp
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  )
}

export default BiometryResendForm
